import { z } from "zod";
import { initContract, authHeadersSchema } from "./base";
import { apiErrorSchema } from "./errors";

const c = initContract();

export const featureSwitchesResponseSchema = z.object({
  switches: z.record(z.string(), z.boolean()),
  effectiveSwitches: z.record(z.string(), z.boolean()),
  /**
   * Optional capability handshake for multiple inline template parts.
   * Older API deployments omit this field.
   */
  supportsStructuredInlineTemplates: z.boolean().optional(),
  /**
   * Optional capability handshake for custom connector OAuth 2.0.
   * Older API deployments omit this field.
   */
  supportsCustomConnectorOAuth2: z.boolean().optional(),
  /**
   * Optional capability handshake for admin-defined model gateways.
   * Older API deployments omit this field.
   */
  supportsCustomModelGateways: z.boolean().optional(),
  /**
   * Optional capability handshake for managed image recognition.
   * Older API deployments omit this field.
   */
  supportsImageRecognition: z.boolean().optional(),
  /**
   * Optional capability handshake for API-backed avatar templates.
   * Older API deployments omit this field.
   */
  supportsAvatarTemplates: z.boolean().optional(),
});

export type FeatureSwitchesResponse = z.infer<
  typeof featureSwitchesResponseSchema
>;

export const updateFeatureSwitchesRequestSchema = z.object({
  switches: z.record(z.string(), z.boolean()),
});

export type UpdateFeatureSwitchesRequest = z.infer<
  typeof updateFeatureSwitchesRequestSchema
>;

/**
 * Zero feature switches contract for /api/zero/feature-switches
 *
 * GET: Get current user's feature switch overrides plus org-scoped overrides.
 * POST: Update feature switch overrides (merge strategy). Some switches may be
 * stored at org scope.
 * DELETE: Remove current user's overrides plus org-scoped overrides controlled
 * by this endpoint.
 */
export const zeroFeatureSwitchesContract = c.router({
  get: {
    method: "GET",
    path: "/api/zero/feature-switches",
    headers: authHeadersSchema,
    responses: {
      200: featureSwitchesResponseSchema,
      401: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Get feature switch overrides",
  },
  update: {
    method: "POST",
    path: "/api/zero/feature-switches",
    headers: authHeadersSchema,
    body: updateFeatureSwitchesRequestSchema,
    responses: {
      200: featureSwitchesResponseSchema,
      400: apiErrorSchema,
      401: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Update feature switch overrides",
  },
  delete: {
    method: "DELETE",
    path: "/api/zero/feature-switches",
    headers: authHeadersSchema,
    body: c.noBody(),
    responses: {
      200: z.object({ deleted: z.literal(true) }),
      401: apiErrorSchema,
      500: apiErrorSchema,
    },
    summary: "Delete feature switch overrides",
  },
});

export type ZeroFeatureSwitchesContract = typeof zeroFeatureSwitchesContract;
